import React from 'react';
import { motion } from 'framer-motion';
import { Award, Users, Globe, Target } from 'lucide-react';
import { mockData } from '../../data/mock';
import { revealVariants, staggerContainer } from '../../utils/motion';
import { useInViewOnce } from '../../hooks/useInViewOnce';

const achievementIcons = [Award, Users, Target, Globe];

export const AchievementsSection = () => {
  const [ref, isInView] = useInViewOnce();

  return (
    <section className="py-20 bg-gradient-to-br from-[#1F3C88] to-[#2d4ba3] relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0 bg-gradient-to-br from-white/10 via-transparent to-white/10" />
      </div>

      {/* Animated Particles */}
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(30)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-white rounded-full opacity-30"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [-30, 30, -30],
              x: [-20, 20, -20],
              opacity: [0.3, 0.8, 0.3]
            }}
            transition={{
              duration: 4 + Math.random() * 6,
              repeat: Infinity,
              ease: "easeInOut",
              delay: Math.random() * 3
            }}
          />
        ))}
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <motion.div
          ref={ref}
          variants={staggerContainer}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="text-center mb-16"
        >
          <motion.div
            variants={revealVariants}
            className="inline-flex items-center space-x-2 bg-white/20 text-white px-4 py-2 rounded-full text-sm font-medium mb-6 backdrop-blur-sm"
          >
            <motion.div 
              className="w-2 h-2 bg-gradient-to-r from-[#FF5C8A] to-[#FF8C42] rounded-full"
              animate={{ scale: [1, 1.5, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
            <span>Our Impact</span>
          </motion.div>
          
          <motion.h2 
            variants={revealVariants}
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            Our <span className="bg-gradient-to-r from-[#FF5C8A] to-[#FF8C42] bg-clip-text text-transparent">Achievements</span>
          </motion.h2>
          
          <motion.p 
            variants={revealVariants}
            className="text-xl text-blue-100 max-w-3xl mx-auto"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            Numbers that reflect our commitment to excellence and the trust our clients place in us 
            across multiple sectors and geographies.
          </motion.p>
        </motion.div>

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
        >
          {mockData.stats.map((stat, index) => {
            const IconComponent = achievementIcons[index];
            return (
              <motion.div
                key={index}
                variants={revealVariants}
                custom={index}
                className="text-center group"
              >
                <motion.div
                  className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 hover:bg-white/15 transition-all duration-500 border border-white/20 relative overflow-hidden"
                  whileHover={{ scale: 1.05, y: -10 }}
                  transition={{ duration: 0.3 }}
                >
                  {/* Glow Effect */}
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-[#FF5C8A]/20 to-[#FF8C42]/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-3xl"
                  />

                  {/* Icon */}
                  <motion.div
                    className="w-16 h-16 bg-gradient-to-r from-[#FF5C8A] to-[#FF8C42] rounded-2xl flex items-center justify-center mx-auto mb-6 relative z-10"
                    initial={{ scale: 0, rotate: -180 }}
                    animate={isInView ? { scale: 1, rotate: 0 } : { scale: 0, rotate: -180 }}
                    transition={{ duration: 0.8, delay: index * 0.2 }}
                    whileHover={{ 
                      rotate: [0, 10, -10, 0],
                      scale: 1.1
                    }}
                  >
                    <IconComponent size={32} className="text-white" />
                  </motion.div>

                  {/* Counter */}
                  <motion.div
                    className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-[#FF5C8A] to-[#FF8C42] bg-clip-text text-transparent mb-3 relative z-10"
                    initial={{ scale: 0 }}
                    animate={isInView ? { scale: 1 } : { scale: 0 }}
                    transition={{ delay: 0.5 + index * 0.2, type: "spring", stiffness: 100 }}
                  >
                    <AnimatedCounter 
                      value={stat.value} 
                      isInView={isInView} 
                      delay={index * 0.2} 
                    />
                  </motion.div>

                  {/* Label */}
                  <div className="text-blue-100 font-medium text-lg relative z-10">
                    {stat.label}
                  </div>

                  {/* Animated Border */}
                  <motion.div
                    className="absolute bottom-0 left-0 h-1 bg-gradient-to-r from-[#FF5C8A] to-[#FF8C42] rounded-full"
                    initial={{ width: 0 }}
                    animate={isInView ? { width: "100%" } : { width: 0 }}
                    transition={{ duration: 1, delay: 1 + index * 0.1 }}
                  />
                </motion.div>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Additional Metrics */}
        <motion.div
          variants={revealVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8"
        >
          {[
            { label: "On-Time Delivery Rate", value: "100%", desc: "Consistent project delivery" },
            { label: "Industry Coverage", value: "5+", desc: "Technology, Mining, Finance, PM, Advisory" },
            { label: "Global Reach", value: "3", desc: "Continents served worldwide" }
          ].map((metric, index) => (
            <motion.div
              key={index}
              className="text-center bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10"
              whileHover={{ scale: 1.02 }}
            >
              <motion.div
                className="text-3xl font-bold text-white mb-2"
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                transition={{ delay: 1.5 + index * 0.1 }}
              >
                {metric.value}
              </motion.div>
              <div className="text-[#FF5C8A] font-semibold mb-1">{metric.label}</div>
              <div className="text-blue-200 text-sm">{metric.desc}</div>
            </motion.div>
          ))}
        </motion.div>

        {/* Bottom Decoration */}
        <motion.div
          className="flex justify-center mt-12"
          variants={revealVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          <motion.div
            className="w-24 h-1 bg-gradient-to-r from-[#FF5C8A] to-[#FF8C42] rounded-full"
            initial={{ scaleX: 0 }}
            animate={isInView ? { scaleX: 1 } : { scaleX: 0 }}
            transition={{ duration: 1, delay: 2 }}
          />
        </motion.div>
      </div>
    </section>
  );
};

// Animated Counter Component
const AnimatedCounter = ({ value, isInView, delay }) => {
  const [count, setCount] = React.useState(0);
  const numericValue = parseInt(value.replace(/[^0-9]/g, ''));
  
  React.useEffect(() => {
    if (!isInView) return;
    
    const timer = setTimeout(() => {
      let start = 0;
      const duration = 2000;
      const increment = numericValue / (duration / 16);
      
      const counter = setInterval(() => {
        start += increment;
        if (start >= numericValue) {
          setCount(numericValue);
          clearInterval(counter);
        } else {
          setCount(Math.floor(start));
        }
      }, 16);
      
      return () => clearInterval(counter);
    }, delay * 1000 + 500);
    
    return () => clearTimeout(timer);
  }, [isInView, numericValue, delay]);
  
  return value.replace(/\d+/, count.toString());
};